import React, { useState, useCallback } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// Icons
// ==========================================

export const CustomTargetIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6366f1"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export type CustomConnectionMethod = "http" | "python" | "custom_app";

export interface CustomTargetConfigValues {
  connectionMethod: CustomConnectionMethod;
  // HTTP
  endpoint: string;
  method: string;
  headers: Array<{ key: string; value: string }>;
  authType: string;
  apiKey: string;
  apiVal: string;
  apiKeyLoc: "header" | "query";
  bearerToken: string;
  basicUser: string;
  basicPass: string;
  bodyTemplate: string;
  responsePath: string;
  // Python
  filePath: string;
  functionName: string;
  pythonExecutable: string;
  workingDir: string;
  configJson: string;
  env: Record<string, string>;
  // Custom App
  command: string;
  // Shared
  timeout: number | string;
}

export const DEFAULT_CUSTOM_TARGET_CONFIG: CustomTargetConfigValues = {
  connectionMethod: "http",
  endpoint: "https://api.example.com/v1/chat",
  method: "POST",
  headers: [{ key: "Content-Type", value: "application/json" }],
  authType: "none",
  apiKey: "Authorization",
  apiVal: "",
  apiKeyLoc: "header",
  bearerToken: "",
  basicUser: "",
  basicPass: "",
  bodyTemplate: '{\n  "prompt": "{{prompt}}"\n}',
  responsePath: "choices[0].message.content",
  filePath: "file://./agent.py",
  functionName: "call_api",
  pythonExecutable: "python3",
  workingDir: "",
  configJson: "{}",
  env: {},
  command: "exec:python /path/to/agent.py",
  timeout: 60,
};

// ==========================================
// Props
// ==========================================

export interface CustomTargetConfigurationProps {
  targetName?: string;
  values: CustomTargetConfigValues;
  onChange: (updates: Partial<CustomTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => void;
  isTestingConnection?: boolean;
  testResult?: {
    success: boolean;
    status?: number | null;
    message?: string;
    response_time?: string;
    sample_response?: string;
    sample_output?: string;
    output?: string;
    error_message?: string | null;
  } | null;
}

// ==========================================
// Emotion Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const cardStyle = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .theme--dark & {
    background: #0f172a;
    border-color: #334155;
  }
`;

const sectionHeadingStyle = css`
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .theme--dark & {
    color: #f8fafc;
  }
`;

const fieldGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const labelStyle = css`
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;

  .theme--dark & {
    color: #cbd5e1;
  }
`;

const inputStyle = css`
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }

  .theme--dark & {
    background: #1e293b;
    border-color: #475569;
    color: #f8fafc;
  }
`;

const textareaStyle = css`
  ${inputStyle};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  resize: vertical;
  min-height: 80px;
`;

const helperTextStyle = css`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 4px;

  .theme--dark & {
    color: #94a3b8;
  }
`;

const methodTabsContainer = css`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 12px;

  .theme--dark & {
    border-color: #334155;
  }
`;

const methodTabButton = (active: boolean) => css`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${active ? "#6366f1" : "transparent"};
  background: ${active ? "rgba(99, 102, 241, 0.08)" : "transparent"};
  color: ${active ? "#4f46e5" : "#64748b"};
  font-weight: ${active ? "700" : "500"};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${active ? "rgba(99, 102, 241, 0.12)" : "rgba(0,0,0,0.03)"};
    color: ${active ? "#4338ca" : "#334155"};
  }

  .theme--dark & {
    color: ${active ? "#818cf8" : "#94a3b8"};
    border-color: ${active ? "#6366f1" : "transparent"};
    background: ${active ? "rgba(99, 102, 241, 0.2)" : "transparent"};
  }
`;

const diagnosticsBox = (success: boolean) => css`
  background: ${success ? "rgba(16, 185, 129, 0.05)" : "rgba(239, 68, 68, 0.05)"};
  border: 1px solid ${success ? "#a7f3d0" : "#fecaca"};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  .theme--dark & {
    background: ${success ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)"};
    border-color: ${success ? "#065f46" : "#991b1b"};
  }
`;

export const CustomTargetConfiguration: React.FC<CustomTargetConfigurationProps> = ({
  targetName = "Custom Target",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult,
}) => {
  const method = values.connectionMethod || "http";

  const handleMethodChange = (newMethod: CustomConnectionMethod) => {
    onChange({ connectionMethod: newMethod });
  };

  const handleHeaderChange = (index: number, key: string, value: string) => {
    const nextHeaders = [...(values.headers || [])];
    nextHeaders[index] = { key, value };
    onChange({ headers: nextHeaders });
  };

  const handleAddHeader = () => {
    onChange({ headers: [...(values.headers || []), { key: "", value: "" }] });
  };

  const handleRemoveHeader = (index: number) => {
    const nextHeaders = (values.headers || []).filter((_, i) => i !== index);
    onChange({ headers: nextHeaders });
  };

  return (
    <div css={pageWrapperStyle}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Heading level={2} style={{ margin: 0, fontSize: "1.45rem", fontWeight: 800 }}>
            Configure {targetName}
          </Heading>
          <p css={helperTextStyle} style={{ margin: "4px 0 0" }}>
            Connect your custom application, HTTP endpoint, or Python agent for active red-team security scanning.
          </p>
        </div>
        {renderModeSwitcher && <div>{renderModeSwitcher}</div>}
      </div>

      {/* Main Configuration Card */}
      <div css={cardStyle}>
        {/* Connection Method Selector */}
        <div css={sectionHeadingStyle}>
          <CustomTargetIcon />
          <span>Connection Method</span>
        </div>

        <div css={methodTabsContainer}>
          <button
            type="button"
            css={methodTabButton(method === "http")}
            onClick={() => handleMethodChange("http")}
          >
            HTTP / HTTPS Endpoint
          </button>
          <button
            type="button"
            css={methodTabButton(method === "python")}
            onClick={() => handleMethodChange("python")}
          >
            Python Agent Script
          </button>
          <button
            type="button"
            css={methodTabButton(method === "custom_app")}
            onClick={() => handleMethodChange("custom_app")}
          >
            Custom Application / Executable
          </button>
        </div>

        {/* 1. HTTP / HTTPS Configuration */}
        {method === "http" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: "16px" }}>
              <div css={fieldGroupStyle}>
                <label css={labelStyle}>Endpoint URL *</label>
                <input
                  type="text"
                  css={inputStyle}
                  value={values.endpoint}
                  onChange={(e) => onChange({ endpoint: e.target.value })}
                  placeholder="https://api.example.com/v1/chat"
                />
              </div>
              <div css={fieldGroupStyle}>
                <label css={labelStyle}>HTTP Method</label>
                <select
                  css={inputStyle}
                  value={values.method || "POST"}
                  onChange={(e) => onChange({ method: e.target.value })}
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
            </div>

            {/* Authentication */}
            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Authentication</label>
              <select
                css={inputStyle}
                value={values.authType || "none"}
                onChange={(e) => onChange({ authType: e.target.value })}
              >
                <option value="none">None</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
                <option value="basic">Basic Auth</option>
              </select>
            </div>

            {values.authType === "bearer" && (
              <div css={fieldGroupStyle}>
                <label css={labelStyle}>Bearer Token</label>
                <input
                  type="password"
                  css={inputStyle}
                  value={values.bearerToken || ""}
                  onChange={(e) => onChange({ bearerToken: e.target.value })}
                  placeholder="Bearer token or secret reference"
                />
              </div>
            )}

            {values.authType === "api_key" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: "12px" }}>
                <div css={fieldGroupStyle}>
                  <label css={labelStyle}>Header Name</label>
                  <input
                    type="text"
                    css={inputStyle}
                    value={values.apiKey || "X-API-Key"}
                    onChange={(e) => onChange({ apiKey: e.target.value })}
                  />
                </div>
                <div css={fieldGroupStyle}>
                  <label css={labelStyle}>Key Value</label>
                  <input
                    type="password"
                    css={inputStyle}
                    value={values.apiVal || ""}
                    onChange={(e) => onChange({ apiVal: e.target.value })}
                  />
                </div>
                <div css={fieldGroupStyle}>
                  <label css={labelStyle}>Location</label>
                  <select
                    css={inputStyle}
                    value={values.apiKeyLoc || "header"}
                    onChange={(e) => onChange({ apiKeyLoc: e.target.value as any })}
                  >
                    <option value="header">Header</option>
                    <option value="query">Query Param</option>
                  </select>
                </div>
              </div>
            )}

            {values.authType === "basic" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div css={fieldGroupStyle}>
                  <label css={labelStyle}>Username</label>
                  <input
                    type="text"
                    css={inputStyle}
                    value={values.basicUser || ""}
                    onChange={(e) => onChange({ basicUser: e.target.value })}
                  />
                </div>
                <div css={fieldGroupStyle}>
                  <label css={labelStyle}>Password</label>
                  <input
                    type="password"
                    css={inputStyle}
                    value={values.basicPass || ""}
                    onChange={(e) => onChange({ basicPass: e.target.value })}
                  />
                </div>
              </div>
            )}

            {/* Headers */}
            <div css={fieldGroupStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label css={labelStyle}>Custom Headers</label>
                <button
                  type="button"
                  style={{ background: "none", border: "none", color: "#6366f1", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}
                  onClick={handleAddHeader}
                >
                  + Add Header
                </button>
              </div>
              {(values.headers || []).map((h, i) => (
                <div key={i} style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <input
                    type="text"
                    css={inputStyle}
                    style={{ flex: 1 }}
                    placeholder="Header Name"
                    value={h.key}
                    onChange={(e) => handleHeaderChange(i, e.target.value, h.value)}
                  />
                  <input
                    type="text"
                    css={inputStyle}
                    style={{ flex: 1 }}
                    placeholder="Header Value"
                    value={h.value}
                    onChange={(e) => handleHeaderChange(i, h.key, e.target.value)}
                  />
                  <button
                    type="button"
                    style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer" }}
                    onClick={() => handleRemoveHeader(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Request Body Template */}
            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Request Body Template (with &#123;&#123;prompt&#125;&#125;)</label>
              <textarea
                css={textareaStyle}
                value={values.bodyTemplate || ""}
                onChange={(e) => onChange({ bodyTemplate: e.target.value })}
                rows={4}
              />
              <span css={helperTextStyle}>
                Use &#123;&#123;prompt&#125;&#125; where the red-team prompt should be injected.
              </span>
            </div>

            {/* Response Path */}
            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Response Extraction Path</label>
              <input
                type="text"
                css={inputStyle}
                value={values.responsePath || ""}
                onChange={(e) => onChange({ responsePath: e.target.value })}
                placeholder="choices[0].message.content or reply"
              />
            </div>
          </div>
        )}

        {/* 2. Python Agent Configuration */}
        {method === "python" && (
          <div>
            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Python File Path *</label>
              <input
                type="text"
                css={inputStyle}
                value={values.filePath || ""}
                onChange={(e) => onChange({ filePath: e.target.value })}
                placeholder="file://./agent.py or ./src/agent.py"
              />
              <span css={helperTextStyle}>
                Path to your Python agent file. Supports relative paths and file:// URIs.
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div css={fieldGroupStyle}>
                <label css={labelStyle}>Invocation Function</label>
                <input
                  type="text"
                  css={inputStyle}
                  value={values.functionName || "call_api"}
                  onChange={(e) => onChange({ functionName: e.target.value })}
                  placeholder="call_api, invoke, run, etc."
                />
                <span css={helperTextStyle}>
                  Defaults to call_api or framework fallback (invoke, run, query).
                </span>
              </div>

              <div css={fieldGroupStyle}>
                <label css={labelStyle}>Python Executable / Virtual Environment</label>
                <input
                  type="text"
                  css={inputStyle}
                  value={values.pythonExecutable || "python3"}
                  onChange={(e) => onChange({ pythonExecutable: e.target.value })}
                  placeholder="python3 or /path/to/venv/bin/python"
                />
              </div>
            </div>

            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Working Directory (Optional)</label>
              <input
                type="text"
                css={inputStyle}
                value={values.workingDir || ""}
                onChange={(e) => onChange({ workingDir: e.target.value })}
                placeholder="./ or /path/to/project"
              />
            </div>

            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Configuration (JSON)</label>
              <textarea
                css={textareaStyle}
                value={values.configJson || "{}"}
                onChange={(e) => onChange({ configJson: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        )}

        {/* 3. Custom Application / Executable */}
        {method === "custom_app" && (
          <div>
            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Command / Executable *</label>
              <input
                type="text"
                css={inputStyle}
                value={values.command || ""}
                onChange={(e) => onChange({ command: e.target.value })}
                placeholder="exec:python /path/to/agent.py or ./script.sh"
              />
              <span css={helperTextStyle}>
                Shell command or path to script/binary executed with prompt passed via standard input.
              </span>
            </div>

            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Working Directory (Optional)</label>
              <input
                type="text"
                css={inputStyle}
                value={values.workingDir || ""}
                onChange={(e) => onChange({ workingDir: e.target.value })}
                placeholder="./ or /path/to/project"
              />
            </div>

            <div css={fieldGroupStyle}>
              <label css={labelStyle}>Configuration (JSON)</label>
              <textarea
                css={textareaStyle}
                value={values.configJson || "{}"}
                onChange={(e) => onChange({ configJson: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Timeout */}
        <div css={fieldGroupStyle}>
          <label css={labelStyle}>Timeout (seconds)</label>
          <input
            type="number"
            css={inputStyle}
            style={{ width: "160px" }}
            value={values.timeout || 60}
            onChange={(e) => onChange({ timeout: Number(e.target.value) || 60 })}
            min={1}
            max={120}
          />
        </div>

        {/* Connection Testing Button & Results */}
        <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
          <Button
            variant="secondary"
            onClick={onTestConnection}
            disabled={isTestingConnection}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
          >
            <WifiIcon />
            {isTestingConnection ? "Testing Target Connection..." : "Test Target Connection"}
          </Button>

          {testResult && (
            <div css={diagnosticsBox(Boolean(testResult.success))}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, color: testResult.success ? "#059669" : "#dc2626" }}>
                {testResult.success ? <CheckIcon /> : <CrossIcon />}
                <span>{testResult.success ? "Connection Successful" : "Connection Failed"}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "10px", fontSize: "0.85rem" }}>
                {testResult.status !== undefined && testResult.status !== null && (
                  <div><strong>Status Code:</strong> {testResult.status}</div>
                )}
                {testResult.response_time && (
                  <div><strong>Response Time:</strong> {testResult.response_time}</div>
                )}
              </div>
              {testResult.error_message && (
                <div style={{ marginTop: "10px", fontSize: "0.85rem", color: "#b91c1c" }}>
                  <strong>Diagnostics:</strong> {testResult.error_message}
                </div>
              )}
              {(testResult.sample_response || testResult.sample_output || testResult.output) && (
                <div style={{ marginTop: "12px", borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: "8px" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
                    Sample Target Response:
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      background: "rgba(0,0,0,0.04)",
                      borderRadius: "6px",
                      fontSize: "0.82rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: "150px",
                      overflowY: "auto",
                      fontFamily: "ui-monospace, monospace",
                    }}
                  >
                    {testResult.sample_response || testResult.sample_output || testResult.output}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      {!hideBottomBar && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
          <Button variant="secondary" onClick={onBack}>
            &lt; Back
          </Button>
          <Button variant="primary" onClick={onNext}>
            Next &gt;
          </Button>
        </div>
      )}
    </div>
  );
};
export default CustomTargetConfiguration;
